import { gapsHit, type GapKind } from "../gaps";
import type { InputFrame } from "../input";
import { railHit, surfaceAt, wallNormal, type QuarterInfo, type Surface } from "../level";
import type { Skater } from "../skater";
import {
  FLIP_TAP_GAP,
  flipVariant,
  MAX_FLIP_ROTATIONS,
  popVariantFor,
  type Dir8,
  type FlipTrick,
  type PopVariant,
} from "../tricks";
import { tryGrind } from "./grind";
import { grabFor, type BodyFlip } from "./rides";
import { land } from "./landing";
import { hitRailInAir, hitWallInAir, stepWall, stepWallride } from "./wall";
import { wrapAngle } from "./math";
import { newAir, type AirState, type FlipState, type GrabState, type SkaterEvent } from "./state";
import {
  ACID_MAX_SPEED,
  ACID_MIN_FALL,
  ACID_POINTS,
  ACID_REACH,
  AIR_FLATTEN_RATE,
  AIR_SPIN_RATE,
  BODY_FLIP_MARGIN,
  BODY_FLIP_MIN_TIME,
  G,
  GRAB_BUFFER,
  GRIND_POP_BUFFER,
  LAND_PREDICT_DT,
  LAND_PREDICT_MAX,
  MOON_GRAVITY,
  SPECIAL_RECOVER,
  STANCE_TAKEOFF,
  SWITCH_BONUS,
  SWITCH_FLIP_TIME,
  TRANSFER_ASSIST,
  TRANSFER_LAND_ANGLE,
  TRANSFER_MAX_SPEED,
  TRANSFER_MIN_SPEED,
  TRANSFER_OUT_CLEAR,
  TRANSFER_POINTS,
  VERT_ALIGN_MAX,
  VERT_ALIGN_RATE,
  VERT_ALIGN_TOL,
  VERT_CARVE_RATE,
  VERT_OLLIE_CHARGE_HEIGHT,
  VERT_OLLIE_HEIGHT,
} from "./tuning";

export function enterAir(sk: Skater, vx: number, vy: number, vz: number): AirState {
  // Leaving the ground ends the revert (or manual) window: no chaining airs without one.
  if (sk.revertWindow > 0 || sk.linkWindow > 0) sk.bankCombo();
  sk.mode = "air";
  sk.landing = null;
  sk.vx = vx;
  sk.vy = vy;
  sk.vz = vz;
  sk.grind = null;
  sk.lip = null;
  if (sk.manual) sk.manualCarry = sk.manual.balance;
  sk.manual = null;
  sk.normal = { x: 0, y: 1, z: 0 };
  sk.vertOllie = null;
  sk.air = newAir(sk.x, sk.z);
  if (sk.stanceTap > 0) {
    // An R2 on flat just before taking off was for the air, not the stance (#134).
    sk.switchStance = !sk.switchStance;
    sk.stanceTap = 0;
    sk.air.earlyRevert = STANCE_TAKEOFF;
  }
  return sk.air;
}

/** The stick sequence that turns the next ollie into a Boneless or Fastplant, if it's still live; used up either way. */
export function takePopVariant(sk: Skater): PopVariant | null {
  const variant = sk.popSeqT > 0 ? popVariantFor(sk.popSeq) : null;
  sk.popSeq = null;
  sk.popSeqT = 0;
  return variant;
}

/** Names an air by how it was popped: nollie (L2), fakie, and a Boneless or Fastplant. */
export function namePop(sk: Skater, air: AirState, fakie: boolean, variant: PopVariant | null): void {
  air.nollie = sk.nollie;
  air.fakie = fakie && !sk.nollie;
  sk.nollie = false;
  if (variant) {
    air.popVariant = variant.id;
    air.popT = 0;
    noteAirTrick(sk, sk.addTrick(variant.name, variant.points));
  }
}

/**
 * Launch speed for a vert air with an ollie on top: VERT_OLLIE_HEIGHT more
 * height, more with the crouch `charge` (0..1), never past `cap`.
 */
export function vertOllieVy(vy: number, charge: number, cap: number): number {
  const k = Math.sqrt(1 + VERT_OLLIE_HEIGHT + VERT_OLLIE_CHARGE_HEIGHT * charge);
  return Math.max(vy, Math.min(cap, vy * k));
}

/** How far (m) a step off the edge of something taller moves you, per step, when you land on its edge. */
const EDGE_STEP = 0.05;

export function stepAir(sk: Skater, input: InputFrame, dt: number, events: SkaterEvent[]): void {
  const air = sk.air!;
  // An ollie pressed for a rail you're about to lock onto (#244); on a wall it's the wallie or the plant, never kept.
  air.popPress = air.wall || air.wallride ? 0 : input.pressed.ollie ? GRIND_POP_BUFFER : Math.max(0, air.popPress - dt);
  if (air.wallride) {
    stepWallride(sk, input, dt, events);
    return;
  }
  if (air.wall) {
    stepWall(sk, input, dt, events);
    return;
  }
  const r2 = input.pressed.revert || air.earlyRevert > 0;
  air.earlyRevert = Math.max(0, air.earlyRevert - dt);
  if (r2) {
    if (air.spineLip && air.vertAxis && sk.y > air.spineLip.cy + 0.1) {
      spineTransfer(sk);
      air.earlyRevert = 0;
    } else if (!air.vertAxis && acidDrop(sk)) air.earlyRevert = 0;
  }
  leaveOverDeck(sk, air);
  if (air.transfer && !air.transfer.ramp && !air.vertAxis && sk.vy < 0) aimTransfer(sk);

  // Spin.
  const spinRate = AIR_SPIN_RATE * sk.special.boost * sk.scale("spin");
  let rate = -input.moveX * spinRate;
  if (air.hop?.button && !input.held[air.hop.button]) air.hop.button = null;
  if (input.held.spinL && air.hop?.button !== "spinL") rate += spinRate;
  if (input.held.spinR && air.hop?.button !== "spinR") rate -= spinRate;
  rate = Math.max(-spinRate * 1.3, Math.min(spinRate * 1.3, rate));
  sk.yaw += rate * dt;
  air.spin += rate * dt;
  lateVertOllie(sk, air, input, dt);
  if (air.vertAxis && rate === 0 && !air.grab?.timed) alignToVert(sk, dt);

  stepFlip(sk, air, input, dt);
  stepGrab(sk, air, input, dt);

  // A special grab stretches out (Christ Air), a normal one (or a spin special) tucks up.
  // A body flip on the bike (#138) rides it as a free air, hands on the bars.
  const stretched = air.grab?.special && !air.grab.timed && air.grab.released === null;
  const flipping = !!air.grab?.timed?.flips;
  const tuck = air.grab && !flipping ? (stretched ? 0.05 : 0.9) : 0.35;
  sk.crouch += (tuck - sk.crouch) * Math.min(1, dt * 10);

  if ((sk.grindBuffer > 0 || input.held.grind) && tryGrind(sk, input, false)) return;

  sk.vy -= airG(sk) * dt;
  let nx = sk.x + sk.vx * dt;
  let nz = sk.z + sk.vz * dt;
  const ny = sk.y + sk.vy * dt;
  if (sk.outOfBounds(nx, nz)) {
    hitWallInAir(sk, nx, nz);
    if (air.wall) return;
    nx = sk.x;
    nz = sk.z;
  }
  const rail = railHit(sk.level, sk.x, sk.z, nx, nz, Math.min(sk.y, ny));
  if (rail) {
    if (hitRailInAir(sk, rail.n, events)) return;
    nx = sk.x;
    nz = sk.z;
  }
  const s = surfaceAt(sk.level, nx, nz);
  if (ny <= s.h) {
    if (sk.y >= s.h - 0.35) {
      sk.x = nx;
      sk.z = nz;
      land(sk, s, events);
      return;
    }
    hitWallInAir(sk, nx, nz);
    if (!air.wall) {
      // Blocked sideways (drifting too slowly into a wall or post to hit
      // it, #124), you stay where you are, so that's the ground you come
      // down on: otherwise every step is blocked and you fall forever.
      const here = surfaceAt(sk.level, sk.x, sk.z);
      if (ny <= here.h && sk.y >= here.h - 0.35) {
        land(sk, here, events);
        return;
      }
      sk.y = ny;
      // Coming down right on the edge of something taller (a vert air that
      // ends against a wall, #78): every step down is blocked and nothing
      // moves you, so you'd fall through the floor. Step off the edge instead
      // (back the way you were going if it can't tell, #124).
      if (here.h > ny + 0.35) {
        const n = wallNormal(sk.level, sk.x, sk.z, sk.x - sk.vx, sk.z - sk.vz);
        sk.x += n.x * EDGE_STEP;
        sk.z += n.z * EDGE_STEP;
      }
    }
    return;
  }
  sk.x = nx;
  sk.y = ny;
  sk.z = nz;
  easeAirNormal(sk, s, dt);
}

/**
 * Flip tricks: one at a time, but you can chain them in one air. Tapping
 * the button again quickly, same direction, makes it a Double / Triple.
 */
function stepFlip(sk: Skater, air: AirState, input: InputFrame, dt: number): void {
  const flipDir = sk.flipAim.dir();
  if (air.flip) {
    const f = air.flip;
    f.t += dt;
    f.sinceTap += dt;
    const tapped = input.pressed.flip && flipDir === f.dir && f.sinceTap <= FLIP_TAP_GAP;
    if (tapped && !f.special && f.rotations < MAX_FLIP_ROTATIONS) {
      upgradeFlip(sk, f);
    } else if (tapped && f.inFlip && f.rotations < 2) {
      doubleWhipInFlip(sk, f);
    } else if (f.t >= f.duration) {
      air.flip = null;
    }
  } else if (input.pressed.flip && air.grab && canAddToFlip(air.grab)) {
    whipInFlip(sk, air, air.grab, flipDir);
  } else if (input.pressed.flip && !air.grab) {
    const body = sk.pullBodyFlip();
    if (body) {
      startBodyFlip(sk, air, body);
      return;
    }
    const special = sk.pullSpecial("flip");
    const base = special?.kind === "flip" ? special : sk.tricks.flip[flipDir];
    const trick = flipVariant(base, 1);
    const index = sk.addTrick(trick.name, trick.points);
    const duration = flipTime(sk, trick);
    air.flip = {
      base,
      trick,
      dir: flipDir,
      rotations: 1,
      index,
      t: 0,
      sinceTap: 0,
      duration,
      special: special !== null,
      inFlip: false,
    };
    noteAirTrick(sk, index);
  }
}

/**
 * A body flip on the bike (#138): it runs by itself like a spin special, hands
 * on the bars, but it's an everyday trick (no meter, no special pose to come
 * back from). It comes round faster to fit the air that's left (#144), so a
 * kicker or a big bunny hop has room for one.
 */
function startBodyFlip(sk: Skater, air: AirState, body: BodyFlip): void {
  const index = sk.addTrick(body.name, body.points);
  const duration = bodyFlipTime(body, timeToLand(sk));
  air.grab = {
    name: body.name,
    index,
    perSecond: 0,
    t: 0,
    special: false,
    released: null,
    timed: { duration, spinRate: 0, invert: false, flips: body.flips, hand: null, whipped: false, grabbed: null },
  };
  noteAirTrick(sk, index);
}

/**
 * How long a body flip takes with `left` seconds of air (#144): its own pace
 * when there's room, else quick enough to finish just before touching down,
 * but never quicker than BODY_FLIP_MIN_TIME a flip (with less air you bail).
 */
export function bodyFlipTime(body: BodyFlip, left: number): number {
  const quickest = Math.min(body.duration, BODY_FLIP_MIN_TIME * Math.abs(body.flips));
  return Math.max(quickest, Math.min(body.duration, left - BODY_FLIP_MARGIN));
}

/**
 * Seconds until the arc from here comes down on the ground below it, roughly:
 * gravity only, no walls, rails or steering. LAND_PREDICT_MAX if it doesn't
 * within that.
 */
export function timeToLand(sk: Skater): number {
  const g = airG(sk);
  let { x, y, z, vy } = sk;
  for (let t = LAND_PREDICT_DT; t <= LAND_PREDICT_MAX; t += LAND_PREDICT_DT) {
    vy -= g * LAND_PREDICT_DT;
    x += sk.vx * LAND_PREDICT_DT;
    y += vy * LAND_PREDICT_DT;
    z += sk.vz * LAND_PREDICT_DT;
    if (y <= surfaceAt(sk.level, x, z).h) return t;
  }
  return LAND_PREDICT_MAX;
}

/** A body flip with the hands on the bars that hasn't whipped or grabbed yet (one or the other, once a flip). */
function canAddToFlip(g: GrabState): boolean {
  return !!g.timed && g.timed.flips !== 0 && g.timed.hand === null && !g.timed.whipped && g.timed.grabbed === null;
}

/**
 * A whip during a body flip (#138) is one trick, as in Dave Mirra: the flip's
 * entry becomes "Backflip Tailwhip" and takes the whip's points too. One whip
 * a flip; a quick second tap doubles it (`doubleWhipInFlip`).
 */
function whipInFlip(sk: Skater, air: AirState, g: GrabState, flipDir: Dir8): void {
  const base = sk.tricks.flip[flipDir];
  const trick = flipVariant(base, 1);
  const e = sk.combo.entries[g.index];
  if (e) {
    sk.combo.rename(g.index, `${e.name} ${trick.name}`);
    sk.combo.addPoints(g.index, trick.points * (sk.ridingSwitch ? SWITCH_BONUS : 1));
  }
  g.timed!.whipped = true;
  air.flip = {
    base,
    trick,
    dir: flipDir,
    rotations: 1,
    index: g.index,
    t: 0,
    sinceTap: 0,
    duration: flipTime(sk, trick),
    special: true,
    inFlip: true,
  };
}

/**
 * A quick second tap on a whip in a body flip (#138) whips it round twice:
 * "Backflip Double Tailwhip", taking the Double's extra points. Once only:
 * a Triple would never come round inside the flip.
 */
function doubleWhipInFlip(sk: Skater, f: FlipState): void {
  const prev = f.trick;
  const next = flipVariant(f.base, 2);
  const e = sk.combo.entries[f.index];
  if (e) {
    sk.combo.rename(f.index, e.name.replace(prev.name, next.name));
    sk.combo.addPoints(f.index, (next.points - prev.points) * (sk.ridingSwitch ? SWITCH_BONUS : 1));
  }
  f.trick = next;
  f.rotations = next.rotations;
  f.duration = flipTime(sk, next);
  f.sinceTap = 0;
}

/**
 * A grab during a body flip (#138), as in Dave Mirra: grab and the stick's
 * direction take a hand or foot off on the way round, one trick with the
 * flip ("Superman Backflip"), worth the grab's points too. One grab a flip,
 * and not with a whip (the hands are busy). It's held to the end of the flip
 * by itself: there's no letting go mid-flip.
 */
function grabInFlip(sk: Skater, g: GrabState, input: InputFrame): void {
  const grab = grabFor(sk.tricks, input.moveX, input.moveY, modifier(input));
  const e = sk.combo.entries[g.index];
  if (e) {
    sk.combo.rename(g.index, `${grab.name} ${e.name}`);
    sk.combo.addPoints(g.index, grab.points * (sk.ridingSwitch ? SWITCH_BONUS : 1));
  }
  g.timed!.grabbed = grab.name;
}

/**
 * Grabs: points keep accruing while the button is held. Let go of a
 * special and it takes a moment to get back on the board.
 */
function stepGrab(sk: Skater, air: AirState, input: InputFrame, dt: number): void {
  const g = air.grab;
  if (g) {
    if (g.timed) {
      // Turning (and flipping over) by itself; done when its time's up.
      if (input.pressed.grab && canAddToFlip(g)) grabInFlip(sk, g, input);
      g.t += dt;
      sk.yaw += g.timed.spinRate * dt;
      if (g.t >= g.timed.duration) air.grab = null;
    } else if (g.released !== null) {
      g.released += dt;
      if (g.released >= SPECIAL_RECOVER) air.grab = null;
    } else if (input.held.grab) {
      g.t += dt;
      sk.combo.addPoints(g.index, g.perSecond * dt);
    } else if (g.special) {
      g.released = 0;
    } else {
      air.grab = null;
    }
  } else if (air.flip) {
    bufferGrab(sk, air, input, dt);
  } else if (input.pressed.grab || air.grabBuffer) {
    // A grab buffered through the flip (#84) reads the stick now, and the
    // special sequence from when it was pressed if that's gone stale since.
    const special = sk.pullSpecial("grab", sk.specialSeq ?? air.grabBuffer?.seq ?? null);
    air.grabBuffer = null;
    const trick = special?.kind === "grab" || special?.kind === "spin" ? special : grabFor(sk.tricks, input.moveX, input.moveY, modifier(input));
    const index = sk.addTrick(trick.name, trick.points);
    const perSecond = "perSecond" in trick ? trick.perSecond : 0;
    // Backside: clockwise from above riding regular, the other way switch.
    const timed =
      special?.kind === "spin"
        ? {
            duration: special.duration,
            spinRate: ((sk.switchStance ? 1 : -1) * special.spin * Math.PI) / 180 / special.duration,
            invert: special.invert,
            flips: special.flips,
            hand: special.hand,
            whipped: false,
            grabbed: null,
          }
        : null;
    air.grab = {
      name: trick.name,
      index,
      perSecond,
      t: 0,
      special: special !== null,
      released: null,
      timed,
    };
    noteAirTrick(sk, index);
  }
}

/** DMFB's modifier (#138): a shoulder held as a grab starts (the ride's tables say whether it does anything). */
function modifier(input: InputFrame): boolean {
  return input.held.spinL || input.held.spinR;
}

/**
 * THPS lets you go from a flip straight into a grab: a grab pressed while the
 * flip spins is queued and starts when it finishes, if grab is still held or
 * was let go within GRAB_BUFFER.
 */
function bufferGrab(sk: Skater, air: AirState, input: InputFrame, dt: number): void {
  const b = air.grabBuffer;
  if (input.pressed.grab) air.grabBuffer = { t: GRAB_BUFFER, seq: sk.specialSeq };
  else if (b && input.held.grab) b.t = GRAB_BUFFER;
  else if (b && (b.t -= dt) <= 0) air.grabBuffer = null;
}

/**
 * The board's "up" in the air. A vert air keeps the wall's normal the whole
 * way (spins turn around it, so a 540 still reads right) until a spine
 * transfer or drifting off the ramp's line ends it. Otherwise it eases flat,
 * and leans into a ramp it's coming down onto (acid drop, far side of a
 * spine) so the landing reads as a smooth re-entry.
 */
export function easeAirNormal(sk: Skater, s: Surface, dt: number): void {
  const air = sk.air!;
  const a = air.vertAxis;
  const q = s.quarter;
  const onLine = !!a && !!q && q.axisX * a.x + q.axisZ * a.z > 0.9;
  if (!onLine) air.vertUp = null;
  const n = sk.normal;
  let tx = 0;
  let ty = 1;
  let tz = 0;
  if (air.vertUp) {
    ({ x: tx, y: ty, z: tz } = air.vertUp);
  } else if (onLine && sk.vy < 0) {
    const l = Math.hypot(s.gx, 1, s.gz);
    tx = -s.gx / l;
    ty = 1 / l;
    tz = -s.gz / l;
  }
  const k = air.vertUp ? 1 : 1 - Math.exp(-AIR_FLATTEN_RATE * dt);
  const x = n.x + (tx - n.x) * k;
  const y = n.y + (ty - n.y) * k;
  const z = n.z + (tz - n.z) * k;
  const l = Math.hypot(x, y, z) || 1;
  sk.normal = { x: x / l, y: y / l, z: z / l };
}

/** How far (rad) the board's heading is off the vert air's line, signed: nose up or down the wall, whichever is closer. */
export function vertOff(sk: Skater): number {
  const a = sk.air!.vertAxis!;
  let d = wrapAngle(sk.yaw - Math.atan2(a.x, a.z));
  if (d > Math.PI / 2) d -= Math.PI;
  if (d < -Math.PI / 2) d += Math.PI;
  return d;
}

/**
 * Off vert, ease the board onto the ramp's line so a small drift comes back
 * down clean (#183: only within VERT_ALIGN_TOL, and no more than
 * VERT_ALIGN_MAX in one air, so a crooked air stays crooked; a bowl
 * corner's carve, `vertCarve`, is given back on top). Only while
 * the stick is let go, so it never fights your steering. This is not player
 * spin, so it never counts toward a 180.
 */
export function alignToVert(sk: Skater, dt: number): void {
  const air = sk.air!;
  const d = vertOff(sk);
  const carve = air.vertCarve;
  if (Math.abs(d) > VERT_ALIGN_TOL + carve) return;
  const rate = carve > 0 ? VERT_CARVE_RATE : VERT_ALIGN_RATE;
  const turn = Math.min(Math.abs(d), rate * dt, VERT_ALIGN_MAX + carve - air.vertAssist);
  if (turn <= 0) return;
  sk.yaw -= Math.sign(d) * turn;
  air.vertAssist += turn;
}

/** Let go of a crouch held through the vert launch, just after it: the ollie still counts. */
function lateVertOllie(sk: Skater, air: AirState, input: InputFrame, dt: number): void {
  const late = air.vertOllieLate;
  if (!late) return;
  if (input.released.ollie) {
    air.vertOllieLate = null;
    const extra = vertOllieVy(late.vy0, late.charge, late.cap) - late.vy0;
    sk.vy += extra;
    namePop(sk, air, late.fakie, takePopVariant(sk));
    return;
  }
  late.t -= dt;
  if (late.t <= 0 || !input.held.ollie) air.vertOllieLate = null;
}

/**
 * R2 off a spine (THPS): arc over the coping and come down the far side's
 * transition, still facing the way you were going. Picks the horizontal
 * speed that lands you partway down the far wall, whenever you press it.
 */
export function spineTransfer(sk: Skater): void {
  const air = sk.air!;
  const lip = air.spineLip!;
  air.spineLip = null;
  air.transferOut = null;
  air.vertUp = null; // heading over to the far side: flatten out
  sk.revertBuffer = 0; // this R2 was the transfer, not a revert on landing
  // Toward the far side is against its up-ramp axis.
  const ox = -lip.axisX;
  const oz = -lip.axisZ;
  const past = lip.r * (1 - Math.sin(TRANSFER_LAND_ANGLE));
  const dist = (lip.cx - sk.x) * ox + (lip.cz - sk.z) * oz + past;
  const targetY = lip.cy - lip.r * Math.cos(TRANSFER_LAND_ANGLE);
  const fall = Math.max(0, sk.y - targetY);
  const g = airG(sk);
  const t = (sk.vy + Math.sqrt(sk.vy * sk.vy + 2 * g * fall)) / g;
  const vh = Math.max(TRANSFER_MIN_SPEED, Math.min(TRANSFER_MAX_SPEED, dist / Math.max(t, 1e-3)));
  const along = sk.vx * ox + sk.vz * oz;
  sk.vx += (vh - along) * ox;
  sk.vz += (vh - along) * oz;
  // Land judged against the far ramp, so you roll down it forward rather than fakie.
  air.vertAxis = { x: lip.axisX, z: lip.axisZ };
  sk.addTrick("Spine Transfer", TRANSFER_POINTS);
}

/**
 * R2 in the air over a transition you're heading down (THPS): snap into the
 * ramp and land partway down it, facing down it, carrying the fall as speed.
 * Looks along the air for the first quarter pipe facing back at you whose
 * transition you can reach, then steers the air onto it the way
 * `spineTransfer` does. Returns false (and does nothing) if there isn't one.
 */
export function acidDrop(sk: Skater): boolean {
  const hs = Math.hypot(sk.vx, sk.vz);
  const f = sk.forward;
  const dx = hs > 0.5 ? sk.vx / hs : f.x;
  const dz = hs > 0.5 ? sk.vz / hs : f.z;
  for (let d = 0; d <= ACID_REACH; d += 0.25) {
    const px = sk.x + dx * d;
    const pz = sk.z + dz * d;
    const q = surfaceAt(sk.level, px, pz).quarter;
    // Only a ramp you'd ride down: its up-ramp axis points back at you.
    if (!q || dx * q.axisX + dz * q.axisZ > -0.5) continue;
    if (steerAcidDrop(sk, px, pz, q)) return true;
  }
  return false;
}

/** Aims the air at the transition of `q` (sampled at px, pz); false if it's out of reach. */
export function steerAcidDrop(sk: Skater, px: number, pz: number, q: QuarterInfo): boolean {
  const air = sk.air!;
  const aim = aimAtTransition(sk, px, pz, q);
  if (!aim) return false;
  const { vAlong, along } = aim;

  sk.revertBuffer = 0; // this R2 was the drop, not a revert on landing
  air.acidCarry = Math.hypot(sk.vx, sk.vz);
  sk.vx += (vAlong - along) * q.axisX;
  sk.vz += (vAlong - along) * q.axisZ;
  // Turn into the ramp's line (nose down it, or tail down it if you're fakie)
  // and judge the landing against it, like a vert air.
  const f = sk.forward;
  const axisYaw = Math.atan2(q.axisX, q.axisZ);
  sk.yaw = f.x * q.axisX + f.z * q.axisZ >= 0 ? axisYaw : axisYaw + Math.PI;
  air.vertAxis = { x: q.axisX, z: q.axisZ };
  sk.addTrick("Acid Drop", ACID_POINTS);
  return true;
}

/**
 * The speed along `q`'s line (and what it is now) that brings the air down
 * its transition partway up, at most `angle` steep (a spine transfer's
 * landing by default); null if there's no such spot to fall onto. Also
 * where the air is along the line now (`uNow`) and how long the fall takes.
 */
function aimAtTransition(
  sk: Skater,
  px: number,
  pz: number,
  q: QuarterInfo,
  angle = TRANSFER_LAND_ANGLE,
): { vAlong: number; along: number; uNow: number; t: number } | null {
  const uAt = (h: number) => (h >= q.r ? q.r : h <= 0 ? 0 : Math.sqrt(q.r * q.r - (q.r - h) ** 2));
  // Along the ramp's axis: where you are, and where you'll come down. Aim
  // about as steep as a spine transfer, but never back up the ramp behind
  // you and never somewhere you can't fall onto.
  const here = (sk.x - px) * q.axisX + (sk.z - pz) * q.axisZ;
  const uNow = q.u + here;
  const uT = Math.min(q.r * Math.sin(angle), uNow, uAt(sk.y - ACID_MIN_FALL));
  const targetY = q.r - Math.sqrt(q.r * q.r - uT * uT);
  if (targetY < 0.15 * q.r) return null; // that low it's just landing on the flat
  const fall = sk.y - targetY;
  const g = airG(sk);
  const t = (sk.vy + Math.sqrt(sk.vy * sk.vy + 2 * g * fall)) / g;
  const vAlong = (uT - uNow) / Math.max(t, 1e-3);
  if (vAlong < -ACID_MAX_SPEED) return null;
  const along = sk.vx * q.axisX + sk.vz * q.axisZ;
  const lx = sk.x + (sk.vx - along * q.axisX) * t + (uT - uNow) * q.axisX;
  const lz = sk.z + (sk.vz - along * q.axisZ) * t + (uT - uNow) * q.axisZ;
  const land = surfaceAt(sk.level, lx, lz);
  const lq = land.quarter;
  if (!lq || lq.axisX * q.axisX + lq.axisZ * q.axisZ < 0.99 || Math.abs(land.h - targetY) > 0.3) return null;
  return { vAlong, along, uNow, t };
}

/**
 * A transfer air (#77) turns into one once it's up past the coping: it takes
 * the speed out over the deck the launch kept for it and stops being a vert
 * air (no vert axis, no auto-180, no staying lined up with the wall), so it
 * flattens as it leaves. Coming back down before it got there, it stays a
 * plain vert air.
 */
function leaveOverDeck(sk: Skater, air: AirState): void {
  const out = air.transferOut;
  if (!out) return;
  if (sk.vy <= 0) {
    air.transferOut = null;
    return;
  }
  if (sk.y < out.clearY) return;
  air.transferOut = null;
  air.vertAxis = null;
  air.vertUp = null;
  air.spineLip = null;
  const along = sk.vx * out.axisX + sk.vz * out.axisZ;
  sk.vx += (out.vh - along) * out.axisX;
  sk.vz += (out.vh - along) * out.axisZ;
  air.transfer = { axisX: out.axisX, axisZ: out.axisZ, ramp: false };
}

/**
 * On the way down a transfer air, looks ahead along it for a quarter pipe
 * you'd ride down (the way an acid drop does) and, if the air is nearly
 * going to come down its transition anyway, nudges it there by at most
 * TRANSFER_ASSIST and judges the landing against the ramp's line.
 */
function aimTransfer(sk: Skater): void {
  const air = sk.air!;
  const hs = Math.hypot(sk.vx, sk.vz);
  if (hs < 0.5) return;
  const dx = sk.vx / hs;
  const dz = sk.vz / hs;
  for (let d = 0; d <= ACID_REACH; d += 0.25) {
    const px = sk.x + dx * d;
    const pz = sk.z + dz * d;
    const q = surfaceAt(sk.level, px, pz).quarter;
    if (!q || dx * q.axisX + dz * q.axisZ > -0.5 || !farRamp(air, q)) continue;
    // The shallowest spot down the transition the air can reach that still
    // clears the coping on the way (else it's a landing on the deck).
    for (const angle of TRANSFER_AIM_ANGLES) {
      const aim = aimAtTransition(sk, px, pz, q, angle);
      if (!aim || Math.abs(aim.vAlong - aim.along) > TRANSFER_ASSIST || !clearsCoping(sk, q, aim)) continue;
      sk.vx += (aim.vAlong - aim.along) * q.axisX;
      sk.vz += (aim.vAlong - aim.along) * q.axisZ;
      air.vertAxis = { x: q.axisX, z: q.axisZ };
      air.transfer!.ramp = true;
      return;
    }
    return;
  }
}

/** Landing spots a transfer air aims for, shallowest first (0 = flat, 90 = vertical). */
const TRANSFER_AIM_ANGLES = [60, 50, 40, 30].map((d) => (d * Math.PI) / 180);

/** Whether an air on `aim` is still above `q`'s coping when it passes over it (always, if it's already past it). */
function clearsCoping(sk: Skater, q: QuarterInfo, aim: { vAlong: number; uNow: number }): boolean {
  if (aim.uNow <= q.r) return true;
  if (aim.vAlong >= 0) return false;
  const tc = (aim.uNow - q.r) / -aim.vAlong;
  // Heights are from the ground, as in aimAtTransition: the coping is at r.
  return sk.y + sk.vy * tc - 0.5 * airG(sk) * tc * tc > q.r + TRANSFER_OUT_CLEAR;
}

export function flipTime(sk: Skater, trick: FlipTrick): number {
  return trick.duration * (sk.switchStance ? sk.switchPenalty(SWITCH_FLIP_TIME) : 1);
}

/** Gravity in the air: the hang time stat lightens it, and so does the Moon gravity cheat (#119). */
export function airG(sk: Skater): number {
  const g = G * sk.scale("hang");
  return sk.cheats.moon ? g * MOON_GRAVITY : g;
}

/** One more rotation on the flip in progress: longer, and worth more. */
export function upgradeFlip(sk: Skater, f: FlipState): void {
  const prev = f.trick;
  const next = flipVariant(f.base, f.rotations + 1);
  const e = sk.combo.entries[f.index];
  if (e) {
    // Scale what was paid (switch bonus, repeat decay) rather than repricing from scratch.
    sk.combo.addPoints(f.index, e.points * (next.points / prev.points - 1));
    sk.combo.rename(f.index, e.name.replace(prev.name, next.name));
  }
  f.trick = next;
  f.rotations = next.rotations;
  f.duration = flipTime(sk, next);
  f.sinceTap = 0;
}

export function noteAirTrick(sk: Skater, index: number): void {
  const air = sk.air!;
  if (air.firstIndex < 0) air.firstIndex = index;
}

/** Scores the air (or grind) gaps from this air's takeoff to here; each air scores once. */
export function airGaps(sk: Skater, kind: GapKind): void {
  const air = sk.air!;
  if (!air.takeoff) return;
  for (const g of gapsHit(sk.gaps, kind, air.takeoff, sk)) sk.scoreGap(g);
  air.takeoff = null;
}

/** A ramp a transfer air (#77) can come down: facing back the way it left, so not the one it launched off. */
export function farRamp(air: AirState, q: QuarterInfo): boolean {
  const t = air.transfer;
  return !!t && q.axisX * t.axisX + q.axisZ * t.axisZ < -0.5;
}
